import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import AppLayout from '../../components/AppLayout';

import ChatTabBar from './components/ChatTabBar';
import ConversationWelcome from './components/ConversationWelcome';
import ConversationLoading from './components/LoadingStates/ConversationLoading';
import ErrorState from './components/LoadingStates/ErrorState';
import MessageArea from './components/MessageArea';
import { useChats } from './hooks/useChats';
import { useConversationNavigation } from './hooks/useConversationNavigation';
import { useOpenedChats } from './hooks/useOpenedChats';
import { Chat } from './types';
import { transformApiChatToUI } from './utils/messageUtils';

const ConversationPage: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>();
  const {
    chats: apiChats,
    loading: chatsLoading,
    creating: chatCreating,
    error: chatsError,
    createNewChat: createChatMutation,
    refreshChats,
  } = useChats();

  const activeChatId = chatId || null;

  const chats: Chat[] = useMemo(() => {
    return apiChats.map(transformApiChatToUI);
  }, [apiChats]);

  const { openedChats, handleCreateNewChat, handleCloseTab } =
    useOpenedChats({
      chats,
      activeChatId,
      createNewChat: createChatMutation,
    });

  const { handleChatNavigation } = useConversationNavigation({
    activeChatId,
    chats,
    chatsLoading,
    chatsError,
    openedChats,
  });

  useEffect(() => {
    if (activeChatId && chats.length > 0) {
      const activeChat = chats.find(chat => chat.id === activeChatId);
      if (activeChat) {
        document.title = `${activeChat.title} - Acconto.ai`;
      }
    } else {
      document.title = 'Conversation - Acconto.ai';
    }
  }, [activeChatId, chats]);

  const renderContent = () => {
    if (chatsLoading) {
      return <ConversationLoading />;
    }

    if (chatsError) {
      return (
        <ErrorState
          title='Error loading conversations'
          message={chatsError}
          onRetry={refreshChats}
        />
      );
    }

    return (
      <div className='flex flex-col h-full min-h-0 bg-gray-50'>
        <div className='flex-shrink-0'>
          <ChatTabBar
            chats={openedChats}
            activeChatId={activeChatId || ''}
            onChatSelect={handleChatNavigation}
            onCreateNewChat={handleCreateNewChat}
            onCloseTab={handleCloseTab}
            isCreating={chatCreating}
            error={chatsError}
          />
        </div>

        <div className='flex-1 flex flex-col overflow-hidden min-h-0'>
          {!activeChatId ? (
            <div className='flex-1 overflow-y-auto min-h-0'>
              <ConversationWelcome
                chats={chats}
                onChatSelect={handleChatNavigation}
              />
            </div>
          ) : (
            <MessageArea activeChatId={activeChatId} />
          )}
        </div>
      </div>
    );
  };

  return <AppLayout>{renderContent()}</AppLayout>;
};

export default ConversationPage;
